import React, { useState, useEffect } from 'react';
import { useWebSocketData } from '../context/WebSocketContext';
import {
    MdCheckCircle,
    MdCancel,
    MdInbox,
    MdDelete,
    MdInfo,
    MdShowChart,
    MdAccessTime,
    MdPerson,
    MdPhone,
    MdLanguage,
    MdEmail,
    MdVisibility,
    MdStar,
    MdRouter,
    MdLocationOn,
    MdMarkEmailRead
} from 'react-icons/md';
import './Leads.css';

function LiveLeads() {
    const [liveLeads, setLiveLeads] = useState([]);
    const [expandedRow, setExpandedRow] = useState(null);
    const topics = useWebSocketData();

    // Listen to leads topic from WebSocket
    useEffect(() => {
        if (topics && topics.leads && topics.leads.length > 0) {
            const newLeads = topics.leads;
            setLiveLeads((prev) => {
                const combined = [...newLeads, ...prev];
                const unique = combined.filter((lead, index, self) =>
                    index === self.findIndex((l) => l.id === lead.id)
                );
                return unique.slice(0, 100); // Keep last 100
            });
        }
    }, [topics]);

    const clearLeads = () => {
        setLiveLeads([]);
    };

    const getEmailBadge = (emailSent) => {
        if (emailSent === true) {
            return <span className="badge-success"><MdCheckCircle className="me-1" /> Sent</span>;
        } else if (emailSent === false) {
            return <span className="badge-danger"><MdCancel className="me-1" /> Failed</span>;
        }
        return <span className="badge bg-secondary">Unknown</span>;
    };

    return (
        <div className="leads-container">
            {/* Modern Header */}
            <div className="leads-header">
                <div>
                    <h3 className="leads-title">
                        <span className="live-dot me-2"></span> Live Leads
                    </h3>
                    <p className="text-muted mb-0">Real-time lead submissions streaming</p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    <span className="badge-custom badge-primary-soft">
                        <MdInbox className="me-1" /> {liveLeads.length} Recent
                    </span>
                    {liveLeads.length > 0 && (
                        <button onClick={clearLeads} className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1">
                            <MdDelete /> Clear
                        </button>
                    )}
                </div>
            </div>

            {/* Info Alert */}
            <div className="alert alert-light border mb-4 d-flex align-items-center shadow-sm">
                <MdInfo className="me-2 text-primary" size={20} />
                <span className="text-muted small">
                    New leads appear automatically. Latest submissions are highlighted at the top.
                </span>
            </div>

            {/* Live Leads Feed */}
            <div className="leads-table-card">
                <div className="p-3 border-bottom d-flex justify-content-between align-items-center bg-light">
                    <h6 className="m-0 fw-bold text-primary">
                        <MdShowChart className="me-2" /> Activity Feed
                    </h6>
                    {liveLeads.length > 0 && (
                        <span className="badge bg-primary rounded-pill">{liveLeads.length}</span>
                    )}
                </div>

                {liveLeads.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="spinner-grow text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Waiting...</span>
                        </div>
                        <h5 className="text-muted">Waiting for new leads...</h5>
                        <p className="text-muted small">Lead submissions will appear here in real-time</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table-custom">
                            <thead>
                                <tr>
                                    <th><MdAccessTime className="me-1" /> Time</th>
                                    <th><MdPerson className="me-1" /> Name</th>
                                    <th><MdPhone className="me-1" /> Phone</th>
                                    <th><MdLanguage className="me-1" /> Domain</th>
                                    <th><MdEmail className="me-1" /> Email Status</th>
                                    <th><MdInfo className="me-1" /> Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {liveLeads.map((lead, index) => (
                                    <React.Fragment key={lead.id || index}>
                                        <tr style={index === 0 ? { background: 'rgba(72, 187, 120, 0.05)' } : {}}>
                                            <td data-label="Time">
                                                <span className="fw-medium text-dark">
                                                    {lead.createdAt
                                                        ? new Date(lead.createdAt).toLocaleTimeString()
                                                        : 'Just now'}
                                                </span>
                                            </td>
                                            <td data-label="Name">
                                                <div className="fw-bold text-primary">
                                                    {lead.name}
                                                    {index === 0 && (
                                                        <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
                                                            NEW
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td data-label="Phone">
                                                <a href={`tel:${lead.phone}`} className="text-decoration-none text-dark fw-medium">
                                                    {lead.phone}
                                                </a>
                                            </td>
                                            <td data-label="Domain">
                                                <code className="text-muted">{lead.domain}</code>
                                            </td>
                                            <td data-label="Email Status">
                                                {lead.emailSent ? (
                                                    <span className="badge-custom badge-success-soft">
                                                        <MdCheckCircle className="me-1" /> Sent
                                                    </span>
                                                ) : (
                                                    <span className="badge-custom badge-danger-soft">
                                                        <MdCancel className="me-1" /> Failed
                                                    </span>
                                                )}
                                            </td>
                                            <td data-label="Details">
                                                <button
                                                    className={`btn-action-square btn-action-view ${expandedRow === index ? 'active' : ''}`}
                                                    type="button"
                                                    onClick={() => setExpandedRow(expandedRow === index ? null : index)}
                                                >
                                                    <MdVisibility />
                                                </button>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td colSpan="6" className="p-0 border-0">
                                                {expandedRow === index && (
                                                    <div className="p-3 bg-light border-bottom small">
                                                        <div className="row g-2">
                                                            <div className="col-md-4">
                                                                {lead.interest && (
                                                                    <div className="mb-1">
                                                                        <MdStar className="text-warning me-1" />
                                                                        <strong>Interest:</strong> {lead.interest}
                                                                    </div>
                                                                )}
                                                                {lead.ip && (
                                                                    <div className="mb-1">
                                                                        <MdRouter className="me-1" />
                                                                        <strong>IP:</strong> <code>{lead.ip}</code>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="col-md-4">
                                                                {lead.geo && (
                                                                    <div className="mb-1">
                                                                        <MdLocationOn className="me-1 text-danger" />
                                                                        <strong>Location:</strong> {lead.geo.city}, {lead.geo.state}, {lead.geo.country}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="col-md-4">
                                                                {lead.emailsSentTo && lead.emailsSentTo.length > 0 && (
                                                                    <div>
                                                                        <MdMarkEmailRead className="me-1 text-success" />
                                                                        <strong>Sent to:</strong> {lead.emailsSentTo.join(', ')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LiveLeads;
